// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { Target } from "@quartzds/core";
import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  Fragment,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import type { LiteralUnion } from "type-fest";

export type Importance = "emphasized" | "standard";
export type Appearance = "outline" | "solid" | "transparent";
export type Layout = "grid" | "list";
export type AspectRatio = "1:1" | "3:2" | "16:9";

@Component({
  tag: "qds-web-card",
  shadow: true,
  styleUrl: "card.css",
})
export class Card implements ComponentInterface {
  @Element() public el: HTMLQdsWebCardElement;

  /**
   * The card's importance.
   */
  @Prop() public importance?: Importance = "standard";

  /**
   * The card's appearance.
   */
  @Prop() public appearance?: Appearance = "solid";

  /**
   * The card's layout.
   */
  @Prop() public layout?: Layout = "grid";

  /**
   * Src of the image to display.
   *
   * @remark To specify the source path or url based on the responsive image srcSet attribute add one or more strings separated by commas, indicating possible image sources for the user agent to use.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#srcset
   *
   * @public
   * @readonly
   * @type {string}
   *
   */
  @Prop() public readonly imageSrc?: string;

  /**
   * Alt text for the image to display.
   */
  @Prop() public readonly imageAlt?: string;

  /**
   * Allows to specify the layout width of the image for each of a list of media conditions. The value should be a string containing a comma-separated list of source size descriptors followed by an optional fallback size.
   * @remark Combined with a srcSet content in the imageSrc property, this provides the ability to automatically select among different images—even images of different orientations or aspect ratios—as the document state changes to match different media conditions.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/sizes
   *
   * @public
   * @readonly
   * @type {string}
   *
   */
  @Prop() public readonly imageSizes?: string;

  /**
   * Src of the video to display.
   */
  @Prop() public readonly videoSrc?: string;

  /**
   * Whether to show a checkbox or not
   */
  @Prop() public readonly selectable?: boolean = false;

  /**
   * The default value for the checkbox
   */
  @Prop() public readonly selected?: boolean = false;

  /**
   * The text that will show in the tooltip of the checkbox
   */
  @Prop() public readonly checkboxLabel?: string;

  /**
   * Whether to show the favorite action button
   */
  @Prop() public readonly showFavorite?: boolean = false;

  /**
   * The default state for the favorite action button
   */
  @Prop() public isFavorite = false;

  /**
   * The text that will show in the tooltip of the favorite button
   */
  @Prop() public readonly favoriteLabel?: string;

  /**
   * The destination URL for the link, if the card have a <qds-title>.
   *
   * @webnative
   */
  @Prop() public readonly href?: string;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string> = "_self";

  /**
   * The aspect ratio of the media (width / height).
   *
   */
  @Prop() public readonly aspectRatio?: AspectRatio = "16:9";

  /**
   * Emitted when the favorite button is clicked.
   */
  @Event({ eventName: "qdsFav", bubbles: false, cancelable: false })
  private readonly clickEmitter!: EventEmitter<{
    isFavorite: boolean;
  }>;

  /**
   * Emitted when a change to the checkbox's state is committed by the user.
   */
  @Event({ eventName: "qdsCheck", bubbles: false, cancelable: false })
  private readonly changeEmitter!: EventEmitter<{
    selected: boolean;
  }>;

  @State() private isHovered: boolean;

  @State() private hasMedia: boolean;

  @State() private hasDefaultSlot = false;

  @State() private isDescriptionExpanded: boolean;

  @State() private hasAssetSlot = false;

  @State() private hasFooterSlot = false;

  @State() private hasTagsSlot = false;

  @State() private titleElement?: HTMLHeadingElement;

  @State() private titleText?: string;

  @State() private isMultipleInteraction?: boolean = false;

  @State() private titleKicker?: string;

  @State() private titleSubtitle?: string;

  @State() private isEmphasizedTitleFocus = false;

  public timeoutId: NodeJS.Timeout | undefined;

  public checkboxElement: HTMLQdsCheckboxElement | undefined = undefined;

  public favoriteElement: HTMLQdsButtonElement | undefined = undefined;

  public mediaContainerElement: HTMLDivElement | undefined = undefined;

  public webMediaElement: HTMLQdsWebMediaElement | undefined = undefined;

  public contentElement: HTMLDivElement | undefined = undefined;

  private titleTag = "h2";

  private readonly prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  get #computedImportance(): string {
    return this.importance === "emphasized" ? this.importance : "standard";
  }

  get #computedAppearance(): string {
    if (this.#computedImportance === "emphasized") return "solid";
    switch (this.appearance) {
      case "outline":
      case "transparent": {
        return this.appearance;
      }
      default: {
        return "solid";
      }
    }
  }

  get #computedLayout(): string {
    if (this.#computedImportance === "emphasized") return "grid";
    switch (this.layout) {
      case "grid":
      case "list": {
        return this.layout;
      }
      default: {
        return "list";
      }
    }
  }

  get #computedAspectRatio(): string | undefined {
    if (this.#computedImportance === "emphasized") return undefined;
    if (this.videoSrc !== undefined && this.videoSrc !== "") return "16:9";
    switch (this.aspectRatio) {
      case "1:1":
      case "3:2":
      case "16:9": {
        return this.aspectRatio;
      }
      default: {
        return "16:9";
      }
    }
  }

  get #computedShowFavorite(): boolean {
    return this.showFavorite !== undefined && this.showFavorite;
  }

  get #computedIsSelectable(): boolean {
    return this.selectable !== undefined && this.selectable;
  }

  get #computedEmptyContent(): boolean {
    return !this.hasDefaultSlot && !this.hasFooterSlot;
  }

  get #computedCursorPointer(): boolean {
    if (
      this.#computedImportance === "emphasized" &&
      (this.href === undefined || this.href === "")
    ) {
      return false;
    }
    return (
      this.isHovered &&
      this.#computedAppearance !== "transparent" &&
      !(this.isMultipleInteraction ?? false)
    );
  }

  get #computedHasHref(): boolean {
    return this.href !== undefined && this.href !== "";
  }

  @Listen("mouseenter")
  public handleMouseOver(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.timeoutId = setTimeout(async () => {
      await this.handleMouseEnter();
    }, 100);
  }

  @Listen("mouseleave")
  public async handleMouseOut(): Promise<void> {
    this.handleMouseLeave();
    if (this.titleElement) {
      this.titleElement.style.textDecoration = "inherit";
    }
    this.isHovered = false;
    if (
      this.#computedImportance === "emphasized" &&
      this.webMediaElement &&
      !this.prefersReducedMotion
    ) {
      await this.webMediaElement.pause();
    }
  }

  @Listen("click")
  public handleCardClick(): void {
    if (
      this.href !== undefined &&
      this.href !== "" &&
      !(this.isMultipleInteraction ?? false)
    ) {
      this.windowOpen();
    }
  }

  @Watch("imageSrc")
  @Watch("videoSrc")
  private handleMediaChange(): void {
    this.hasMedia =
      (this.imageSrc !== undefined && this.imageSrc !== "") ||
      (this.videoSrc !== undefined && this.videoSrc !== "");
  }

  @Watch("href")
  private handleHrefChange(): void {
    if (this.#computedHasHref && this.titleElement) {
      this.titleElement.style.cursor = "pointer";
      this.titleElement.addEventListener("click", this.windowOpen);
      this.titleElement.addEventListener("mouseenter", this.underlineTitle);
      this.titleElement.addEventListener("mouseleave", this.removeUnderline);
    } else if (this.titleElement) {
      this.titleElement.style.cursor = "inherit";
      this.titleElement.removeEventListener("mouseenter", this.underlineTitle);
      this.titleElement.removeEventListener("click", this.windowOpen);
    }
  }

  public windowOpen = (): void => {
    window.open(this.href, this.target);
  };

  public underlineTitle = (): void => {
    if (this.titleElement) {
      this.titleElement.style.textDecoration = "underline";
    }
  };

  public removeUnderline = (): void => {
    if ((this.isMultipleInteraction ?? false) && this.titleElement) {
      this.titleElement.style.textDecoration = "inherit";
    }
  };

  // debounce effect
  public async handleMouseEnter(): Promise<void> {
    if (
      this.webMediaElement &&
      this.#computedAppearance !== "transparent" &&
      this.#computedImportance === "emphasized" &&
      !this.prefersReducedMotion
    ) {
      await this.webMediaElement.play();
    }

    if (
      this.href !== undefined &&
      this.href !== "" &&
      this.#computedAppearance !== "transparent"
    ) {
      this.isHovered = true;
      if (!(this.isMultipleInteraction ?? false)) {
        this.underlineTitle();
      }
    }
    if (this.#computedImportance === "emphasized") {
      this.isHovered = true;
    }
  }

  // debounce effect
  public handleMouseLeave(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/class-methods-use-this
  public handleContentClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    const targetTagName = target.tagName.toLowerCase();
    if (
      targetTagName === "button" ||
      targetTagName === "qds-button" ||
      targetTagName === "qds-standalone-link"
    ) {
      event.stopPropagation();
    }
  };

  public componentDidLoad(): void {
    this.handleTitleHeading();
    this.handleHrefChange();
    this.checkboxElement?.addEventListener(
      "qdsChange",
      this.handleCheckboxChange,
    );
    this.contentElement?.addEventListener("click", this.handleContentClick);
  }

  public disconnectedCallback(): void {
    this.mediaContainerElement?.removeEventListener(
      "click",
      this.handleMediaClick,
    );
    this.checkboxElement?.removeEventListener(
      "qdsChange",
      this.handleCheckboxChange,
    );
    this.contentElement?.removeEventListener("click", this.handleContentClick);
    if (this.titleElement) {
      this.titleElement.removeEventListener("mouseleave", this.removeUnderline);
      this.titleElement.removeEventListener("click", this.windowOpen);
      this.titleElement.removeEventListener("mouseenter", this.underlineTitle);
    }
  }

  public connectedCallback(): void {
    this.handleMediaChange();
  }

  public render() {
    return (
      <Host
        class={{
          "qds-theme-dark qds-web-emphasized":
            this.#computedImportance === "emphasized",
        }}
      >
        <div
          class={{
            "qds-web-wrapper": true,
            "qds-web-card-hover":
              this.isHovered && this.#computedAppearance !== "transparent",
            "qds-web-card-pointer": this.#computedCursorPointer,
            "qds-web-wrapper-grid": this.#computedLayout === "grid",
          }}
        >
          {this.titleText !== undefined && this.titleText !== "" && (
            <div>
              <this.titleTag class="qds-web-title-tag">
                <a
                  class="qds-web-title-tag-link"
                  onFocus={() => {
                    this.handleTitleLinkFocus();
                  }}
                  onBlur={() => {
                    this.handleTitleLinkBlur();
                  }}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(this.href === undefined || this.href === ""
                    ? {}
                    : { href: this.href })}
                >
                  {this.titleText}
                </a>
              </this.titleTag>
              <p class="qds-sr-only">
                {this.titleKicker} {this.titleSubtitle}
              </p>
            </div>
          )}
          <div
            class={{
              "qds-web-card": true,
              "qds-web-no-media": !this.hasMedia && !this.hasAssetSlot,
              "qds-web-empty-content": this.#computedEmptyContent,
              "qds-web-transparent-pointer": this.#computedHasHref,
            }}
            data-importance={this.#computedImportance}
            data-appearance={this.#computedAppearance}
            data-layout={this.#computedLayout}
            data-ratio={this.#computedAspectRatio}
          >
            {!(
              this.#computedLayout === "list" &&
              this.hasAssetSlot &&
              !this.hasMedia
            ) && (
              <div
                class="qds-web-media-section"
                ref={(element: HTMLDivElement | undefined) => {
                  if (this.videoSrc !== undefined && this.videoSrc !== "") {
                    element?.addEventListener("click", this.handleMediaClick);
                  } else {
                    element?.removeEventListener(
                      "click",
                      this.handleMediaClick,
                    );
                  }
                  this.mediaContainerElement = element;
                }}
              >
                {this.hasMedia && (
                  <qds-web-media
                    class={this.mediaHideControls()}
                    image-src={this.imageSrc}
                    image-alt={this.imageAlt ?? ""}
                    video-src={this.videoSrc}
                    image-sizes={this.imageSizes}
                    label-name={this.titleText}
                    loading="lazy"
                    muted={this.#computedImportance === "emphasized"}
                    open-modal={this.#computedImportance !== "emphasized"}
                    loop={this.#computedImportance === "emphasized"}
                    aria-hidden={
                      this.#computedImportance === "emphasized"
                        ? "true"
                        : "false"
                    }
                    ref={(element: HTMLQdsWebMediaElement | undefined) => {
                      this.webMediaElement = element;
                    }}
                  />
                )}
                <div
                  class={{
                    "qds-web-actions flex":
                      this.#computedIsSelectable || this.#computedShowFavorite,
                    "qds-web-is-selectable": this.#computedIsSelectable,
                    "qds-web-empty-actions":
                      !this.#computedIsSelectable &&
                      !this.#computedShowFavorite,
                    hide: !this.#computedIsSelectable && !this.hasMedia,
                  }}
                >
                  {this.#computedIsSelectable &&
                    (this.hasMedia || !this.hasAssetSlot) && (
                      <>
                        <qds-checkbox
                          checked={this.selected}
                          onClick={(event: MouseEvent) => {
                            event.stopPropagation();
                          }}
                          ref={(element: HTMLQdsCheckboxElement) => {
                            this.checkboxElement = element;
                          }}
                        />
                        {this.checkboxElement && (
                          <qds-tooltip
                            placement="right"
                            target={this.checkboxElement}
                          >
                            {this.checkboxLabel}
                          </qds-tooltip>
                        )}
                      </>
                    )}
                  {this.hasMedia && this.#computedShowFavorite && (
                    <>
                      <qds-button
                        class="qds-web-favorite"
                        size="small"
                        importance="subdued"
                        icon-name={this.isFavorite ? "star-on" : "star-off"}
                        icon-library="core"
                        text={this.favoriteLabel}
                        onClick={(event: MouseEvent) => {
                          this.handleFavoriteChange(event);
                        }}
                        ref={(element: HTMLQdsButtonElement) => {
                          this.favoriteElement = element;
                        }}
                        icon-only
                      />
                      {this.favoriteElement && (
                        <qds-tooltip
                          placement="left"
                          target={this.favoriteElement}
                        >
                          {this.favoriteLabel}
                        </qds-tooltip>
                      )}
                    </>
                  )}
                </div>

                {this.#computedImportance === "emphasized" && (
                  <div
                    class={{
                      "qds-web-emphasized-content": true,
                      "qds-web-justify-between": this.hasTagsSlot,
                    }}
                  >
                    <slot name="tags" onSlotchange={this.handleSlotChange} />
                    <div class="qds-web-content-wrapper">
                      <div
                        class={{
                          "qds-emphasized-expand": this.isDescriptionExpanded,
                          "qds-emphasized-focused": this.isEmphasizedTitleFocus,
                        }}
                      >
                        <slot onSlotchange={this.handleSlotChange} />
                      </div>
                      <slot
                        name="footer"
                        onSlotchange={this.handleSlotChange}
                      />
                    </div>
                  </div>
                )}

                <div
                  class={{
                    "qds-web-logo": true,
                    hide: !this.hasMedia,
                  }}
                >
                  <slot name="logo" />
                </div>
              </div>
            )}

            <div
              class={{
                "qds-web-asset flex": true,
                hide: this.hasMedia || !this.hasAssetSlot,
              }}
            >
              <slot name="asset" onSlotchange={this.handleSlotChange} />
            </div>

            <div
              class={{
                hide: this.#computedImportance === "emphasized",
                "qds-web-content flex": true,
                "qds-web-has-asset": this.hasAssetSlot && !this.hasMedia,
              }}
              ref={(element: HTMLDivElement | undefined) => {
                this.contentElement = element;
              }}
            >
              <slot onSlotchange={this.handleSlotChange} />
              <div
                class={{
                  hide: !this.hasFooterSlot,
                }}
              >
                <slot name="footer" onSlotchange={this.handleSlotChange} />
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }

  private readonly handleTitleLinkFocus = (): void => {
    this.underlineTitle();
    if (this.#computedImportance === "emphasized") {
      this.isEmphasizedTitleFocus = true;
    }
  };

  private readonly handleTitleLinkBlur = (): void => {
    if (this.titleElement) {
      this.titleElement.style.textDecoration = "inherit";
    }
    if (this.#computedImportance === "emphasized") {
      this.isEmphasizedTitleFocus = false;
    }
  };

  private mediaHideControls(): string {
    return this.#computedImportance === "emphasized" ? "qds-hide-controls" : "";
  }

  private readonly handleMediaClick = (event: MouseEvent): void => {
    if (this.#computedImportance !== "emphasized") {
      event.stopPropagation();
    }
  };

  private readonly handleCheckboxChange = (event: Event): void => {
    event.stopImmediatePropagation();
    const isChecked = this.checkboxElement?.checked ?? false;
    this.changeEmitter.emit({
      selected: isChecked,
    });
  };

  private readonly handleTitleHeading = (): void => {
    const headingSelector = ":is(h1, h2, h3, h4, h5, h6)";
    const titleShadowRoot = this.el.querySelector("qds-title")?.shadowRoot;
    const titleheading = titleShadowRoot?.querySelector(headingSelector);
    this.titleElement = titleheading as HTMLHeadingElement;
  };

  private readonly handleTitleElement = (): void => {
    const titleElement = this.el.querySelector("qds-title");
    if (titleElement) {
      this.titleTag = titleElement.getAttribute("tag") ?? "h2";
      titleElement.setAttribute("aria-hidden", "true");
      this.titleText = titleElement.textContent ?? "";
      this.titleKicker = titleElement.getAttribute("kicker") ?? "";
      this.titleSubtitle = titleElement.getAttribute("subtitle") ?? "";
    } else {
      this.titleText = "";
      this.titleKicker = "";
      this.titleSubtitle = "";
    }
  };

  private readonly handleFavoriteChange = (event: Event): void => {
    event.stopPropagation();
    this.isFavorite = !this.isFavorite;
    this.clickEmitter.emit({
      isFavorite: this.isFavorite,
    });
  };

  private readonly handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    switch (slot.name) {
      case "": {
        this.handleTitleElement();
        this.hasDefaultSlot = slot.assignedElements().length > 0;
        const linkElements = this.el.querySelectorAll("[href]");
        this.isMultipleInteraction = linkElements.length > 0;
        this.isDescriptionExpanded = slot.assignedElements().length > 1;
        break;
      }
      case "asset": {
        this.hasAssetSlot = slot.assignedElements().length > 0;
        break;
      }
      case "footer": {
        this.hasFooterSlot = slot.assignedElements().length > 0;
        break;
      }
      case "tags": {
        this.hasTagsSlot = slot.assignedElements().length > 0;
        break;
      }
      default:
    }
  };
}
